import { formatTime, timeToMinutes, formatDelayHuman } from '../utils/time';
import stopsData from '../data/stops.json';
import ZTM_API_BASE from './ztmApi';

const API_BASE = ZTM_API_BASE;

// Simple in-memory cache for fetched route schedules (for Gdansk mostly, as Gdynia has its own cache)
const timelineCache = new Map();

/**
 * Zwraca zoptymalizowaną mapę przystanków: stopId -> { stopDesc, stopLat, stopLon }
 */
let stopsInfoMap = null;
function getStopsInfoMap() {
    if (stopsInfoMap) return stopsInfoMap;
    stopsInfoMap = new Map();
    for (const stop of stopsData) {
        stopsInfoMap.set(stop.stopId, {
            stopDesc: stop.stopDesc,
            stopLat: stop.stopLat,
            stopLon: stop.stopLon,
        });
    }
    return stopsInfoMap;
}

/**
 * Helper do konwersji czasu "HH:MM:SS" (string) na obiekt dzisiejszej daty z tą godziną
 */
function parseScheduleTimeObj(timeStr) {
    if (!timeStr) return null;
    const [h, m, s] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(h, m, s || 0, 0);
    return date;
}

/**
 * Główna funkcja pobierająca oś czasu (timeline) dla podanego pojazdu.
 * 
 * @param {Object} vehicle - obiekt pojazdu ze stanów (z properties: tripId, routeId, delay, isApproximated itp)
 * @param {string} dateStr - data w formacie YYYY-MM-DD
 * @returns {Promise<Array>} - tablica obiektów reprezentujących przystanki na trasie
 */
export async function getVehicleRouteTimeline(vehicle, dateStr) {
    if (!vehicle || !vehicle.tripId || !vehicle.routeId) {
        throw new Error("Pojazd nie posiada wystarczających danych do wyświetlenia trasy (brak tripId/routeId).");
    }

    const { tripId, routeId, delay = 0, vehicleService } = vehicle;
    const cacheKey = `${dateStr}_${routeId}_${tripId}_${vehicleService || 'any'}`;

    if (timelineCache.has(cacheKey)) {
        return processTimelineWithCurrentTime(timelineCache.get(cacheKey), delay);
    }

    try {
        // Fetch stopTimes for the route
        const res = await fetch(`${API_BASE}/stopTimes?date=${dateStr}&routeId=${routeId}`);
        const data = await res.json();
        const stopTimes = data.stopTimes || [];

        // Zbieramy przystanki o zadanym tripId. Często Gdynia pakuje w jedno zapytanie wiele kursów w obrębie dnia
        const initialStops = stopTimes.filter(s => s.tripId === tripId);

        // Grupujemy przystanki po "busServiceName" i "order" by wylistować fizyczne, zróżane względem czasu osobne kursy.
        const tripsMap = new Map();
        for (const st of initialStops) {
            const key = `${st.busServiceName || 'none'}_${st.order || 0}`;
            if (!tripsMap.has(key)) {
                tripsMap.set(key, []);
            }
            tripsMap.get(key).push(st);
        }
        
        let trips = Array.from(tripsMap.values());

        // Jeśli pojazd ma zdefiniowany `vehicleService` (Gdynia), celujemy najpierw w niego
        if (vehicleService) {
            const withMatchedService = trips.filter(t => t.length > 0 && t[0].busServiceName === vehicleService);
            if (withMatchedService.length > 0) trips = withMatchedService;
        }

        if (trips.length === 0) {
            throw new Error("Nie znaleziono rozkładu na dzisiaj dla tego pojazdu.");
        }

        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
        
        // Z dostępnych kursów tej służby wybieramy jeden, z którym pojazd aktualnie wchodzi w zderzenie w czasie.
        let bestTrip = null;
        let bestDiff = Infinity;

        for (const trip of trips) {
            trip.sort((a, b) => a.stopSequence - b.stopSequence);
            const firstStop = trip[0];
            const lastStop = trip[trip.length - 1];

            if (!firstStop.departureTime || !lastStop.departureTime) continue;

            const timeStrFirst = firstStop.departureTime.split('T')[1];
            const timeStrLast = lastStop.departureTime.split('T')[1];

            const toMin = (timeStr, isNextDayCode) => {
                 const [h, m, s] = timeStr.split(':').map(Number);
                 return h * 60 + m + (s || 0) / 60 + (isNextDayCode ? 24 * 60 : 0);
            };

            const isNextDayStart = firstStop.departureTime.startsWith('1899-12-31');
            const isNextDayEnd = lastStop.departureTime.startsWith('1899-12-31');

            const startMin = toMin(timeStrFirst, isNextDayStart);
            const endMin = toMin(timeStrLast, isNextDayEnd);
            
            // Adjust time window for late night operations 
            let adjNow = nowMin;
            if (nowMin <= 240 && endMin >= 1200) {
                 adjNow += 1440;
            }

            let diff = 0;
            if (adjNow < startMin - 60) {
                 diff = (startMin - 60) - adjNow; // Czeka jako przyszły trip
            } else if (adjNow > endMin + 60) {
                 diff = adjNow - (endMin + 60); // Już się zakończył 
            } else {
                 diff = 0; // Idealnie, obecnie trwa czasowo 
            }
            
            if (diff < bestDiff) {
                 bestDiff = diff;
                 bestTrip = trip;
            }
        }

        const tripStops = bestTrip || trips[0];

        const stopsMap = getStopsInfoMap();

        // Mapujemy na nasz model TimelineStop
        const timeline = tripStops.map((stop, index) => {
            // Czas w formacie rrrr-mm-ddTSS:MM:SS dla departures, my parsujemy string albo ucinamy "T"
            const timeStr = stop.departureTime ? stop.departureTime.split('T')[1] : null;
            const stopInfo = stopsMap.get(stop.stopId) || {};
            
            return {
                id: `${stop.stopId}_${index}`,
                stopId: stop.stopId,
                stopSequence: stop.stopSequence,
                stopName: stopInfo.stopDesc || `Przystanek ${stop.stopId}`,
                stopLat: stopInfo.stopLat || null,
                stopLon: stopInfo.stopLon || null,
                scheduledTime: timeStr,
                isNextDay: false, // Uproszczenie dla Gdańska.
                passenger: stop.passenger !== false,
            };
        });

        // Wyrzucamy techniczne (bez pasażerów jeżeli trzeba, ale na osi z reguły lepiej zostawić wszystkie)

        // Zapis do cache
        timelineCache.set(cacheKey, timeline);

        return processTimelineWithCurrentTime(timeline, delay);
    } catch (error) {
        console.error("Błąd pobierania trasy pojazdu:", error);
        throw error;
    }
}

/**
 * Przetwarza cache'owaną tablicę timeline o bieżące opóźnienie i wylicza czas pozostały.
 */
function processTimelineWithCurrentTime(timeline, delayInSeconds) {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    const delayMin = delayInSeconds / 60;

    return timeline.map((stop, index) => {
        if (!stop.scheduledTime) {
            return { ...stop, realTimeStr: null, status: 'unknown' };
        }

        // Calculate real time
        const [h, m, s] = stop.scheduledTime.split(':').map(Number);
        const scheduledMin = h * 60 + m + (s || 0) / 60;
        const realMin = scheduledMin + delayMin;

        // "estimatedTime": HH:MM string for display
        const estH = Math.floor(realMin / 60) % 24;
        const estM = Math.floor(realMin % 60);
        const realTimeStr = `${String(estH).padStart(2, '0')}:${String(estM).padStart(2, '0')}`;

        // Calculate remaining minutes
        // Diff between realMin and nowMin
        let remainingMin = realMin - nowMin;
        
        // Handle midnight crossing simply
        if (remainingMin < -1000) remainingMin += 24 * 60;
        if (remainingMin > 1000) remainingMin -= 24 * 60;

        let status = 'future';
        if (remainingMin < -1) {
            status = 'past'; // Odjechał
        } else if (remainingMin <= 0) {
            status = 'current'; // Teraźniejszość / na przystanku
        }

        return {
            ...stop,
            realTimeStr,
            remainingMin: Math.ceil(remainingMin),
            status,
        };
    });
}
